import { motion } from 'framer-motion';
import { Button, Flex, Text } from '@chatsdet/ui';

interface SectionSaveFooterProps {
  isSaving: boolean;
  /** Blocks the save while the section's own validation is failing. */
  canSave?: boolean;
  /** Replaces the default "Unsaved changes" note (e.g. a validation message). */
  message?: string;
  onSave: () => void;
  onDiscard: () => void;
}

/**
 * The footer a settings section shows once it has unsaved changes. Callers pass
 * it to `SettingsSection`'s `footer` slot only while dirty, so a section at
 * rest shows no disabled button sitting in dead space.
 */
export function SectionSaveFooter({
  isSaving,
  canSave = true,
  message,
  onSave,
  onDiscard,
}: SectionSaveFooterProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18 }}
    >
      <Flex align="center" justify="between" gap="sm">
        <Text size="sm" className="text-muted-foreground text-pretty">
          {message ?? 'Unsaved changes'}
        </Text>
        <Flex align="center" gap="sm">
          <Button variant="ghost" size="sm" onPress={onDiscard} isDisabled={isSaving}>
            Discard
          </Button>
          <Button
            variant="primary"
            size="sm"
            onPress={onSave}
            isDisabled={isSaving || !canSave}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Flex>
      </Flex>
    </motion.div>
  );
}
